import { useState } from "react";
import Dialog from "@mui/material/Dialog";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { FocusMapDuotoneIcon, MergeOrderDuotoneIcon, ResetViewportDuotoneIcon, AreaCoverageDuotoneIcon, AssignOrdersDuotoneIcon, UnassignOrderDuotoneIcon, ShowRouteDuotoneIcon, ThemeShiftIcon, DocsIcon, DropoffDuotoneIcon, DriverDuotoneIcon, GroupDropoffDuotoneIcon, MergePublishOrderDuotoneIcon } from "components/svg";
import { LocationSearch } from "./svg";

const Shortcut = ({ children }) => (
    <kbd className="inline-block bg-slate-100 text-slate-600 text-xs font-sans font-medium px-2 py-1 rounded-md border border-slate-300/80">
        {children}
    </kbd>
);

export default function BigBrotherDocs() {
    const [openD, setOpenD] = useState(false);
    const [lang, setLang] = useState("en");

    const toggleLang = () => {
        setLang((prev) => (prev === "en" ? "ar" : "en"));
    };

    const docContent = {
        en: {
            dir: "ltr",
            title: "BigBrother Documentation",
            buttonText: "العربية",
            tableHeaders: {
                icon: "Icon",
                action: "Action",
                description: "Description",
                shortcut: "Shortcut"
            },
            sections: [
                {
                    title: "Overview",
                    content:
                        "The BigBrother Map (BB Map) is a powerful, centralized tool within the Barq Admin Portal, designed for real-time monitoring and management of logistics operations. Built for efficiency, the BB Map gives a clear visual overview of the supply chain, showing orders, drivers (called Knights), and their locations all in one interactive dashboard. The interface is divided into three main parts: the Orders Section, the Knights Section, and the Central Map, each offering specific tools to help manage logistics more smoothly.",
                },
                {
                    title: "1. Orders Section",
                    content:
                        "This section is dedicated to managing all incoming and ongoing orders. It empowers users with tools to search, filter, select, and track orders with ease. The layout is intuitive, and order cards are designed to surface critical order details at a glance. The structure of the order cards includes the following elements:",
                    tags: [
                        {
                            classes: "w-fit text-[#3c4b64] text-[15px] px-1.5 py-0.5 rounded-md mt-2 font-semibold",
                            tag: "The top bar of each order card displays key information including the tracking ID, order type, merge status (represented by an 'M' icon), and a color-coded bullet indicating the shipment group."
                        },
                        {
                            classes: "w-fit text-[#3c4b64] text-[15px] px-1.5 py-0.5 rounded-md mt-2 font-semibold",
                            tag: "An eye icon button is available on each card, enabling users to quickly toggle detailed views of the order."
                        },
                        {
                            classes: "w-fit text-[#3c4b64] text-[15px] px-1.5 py-0.5 rounded-md mt-2 font-semibold",
                            tag: "Service Level Agreement (SLA) status is clearly shown in the bottom-right corner of the card, helping ensure timely deliveries."
                        },
                        {
                            classes: "w-fit text-[#3c4b64] text-[15px] px-1.5 py-0.5 rounded-md mt-2 font-semibold",
                            tag: "Newly received orders appear with a ping animation to visually draw attention for immediate review."
                        },
                        {
                            classes: "w-fit text-[#3c4b64] text-[15px] px-1.5 py-0.5 rounded-md mt-2 font-semibold",
                            tag: "To manage an order, simply tap on the respective order card. Selected orders are added to the 'Selected Orders' list, enabling easy bulk actions and tracking."
                        }
                    ],
                    subsections: [
                        { title: "Top Bar", description: "Contains a global search bar and quick navigation buttons like 'Go to Top' and 'Go to Bottom'." },
                        { title: "Filters", description: "Dynamically narrow down the order list by criteria such as Shipment Status, Merchant, Hub, and Type." },
                        { title: "Selected Orders", description: "A temporary workspace where clicked orders appear, allowing you to group them for bulk actions." },
                        { title: "Orders List", description: "The main feed of order cards. Clicking a card adds it to the 'Selected Orders' list." },
                    ],
                },
                {
                    title: "2. Knights (Drivers) Section",
                    content:
                        "This section mirrors the functionality of the Orders Section but is focused entirely on the management of your logistics personnel, referred to as Knights. It allows for real-time tracking and intelligent management of drivers' statuses and assignments.",
                    subsections: [
                        { title: "Top Bar", description: "Includes a search bar for finding specific drivers and navigation buttons." },
                        { title: "Filters", description: "Refine the driver list based on attributes like City, Device status, and operational Status (Available, On-Task, etc.)." },
                        { title: "Selected Drivers", description: "A list to hold selected drivers, typically for assigning tasks to them." },
                        { title: "Drivers List", description: "The main feed of driver cards, showing their current status and availability." },
                    ],
                },
                {
                    title: "3. Map Section & Actions",
                    content:
                        "The Central Map is the operational heart of the BB Map interface. It provides a live, visual representation of orders, drivers, and geographical operations. With built-in controls and tools, it allows users to manage field logistics through direct map interaction. Below are the available functionalities, categorized under map tags and toolbar actions.",
                    tags: [
                        {
                            classes: "w-fit text-[#3c4b64] text-[15px] px-1.5 py-0.5 rounded-md mt-2 font-semibold",
                            tag: "Use the 'Select Area' feature by holding the Ctrl key and clicking and dragging your mouse to draw a selection area on the map. All orders within the selected region will be selected automatically."
                        },
                        {
                            classes: "w-fit text-[#3c4b64] text-[15px] px-1.5 py-0.5 rounded-md mt-2 font-semibold",
                            tag: "Tapping on any icon (e.g., driver or drop-off pin) on the map will automatically add it to your selection list for further actions."
                        },
                        {
                            classes: "w-fit text-[#3c4b64] text-[15px] px-1.5 py-0.5 rounded-md mt-2 font-semibold",
                            tag: "Selected drop-offs on the map are animated with a bounce effect to provide a visual indicator of their status."
                        },
                    ],
                    actions: [
                        {
                            icon: <DropoffDuotoneIcon />,
                            title: "Shipments (Toggle)",
                            description: "Toggle the visibility of shipment group icons on the map. Useful for reducing visual clutter during analysis.",
                            shortcut: null,
                        },
                        {
                            icon: <DriverDuotoneIcon />,
                            title: "Drivers (Toggle)",
                            description: "Enable or disable the visibility of all driver icons on the map for real-time operational awareness.",
                            shortcut: null,
                        },
                        {
                            icon: <GroupDropoffDuotoneIcon />,
                            title: "Drop-Offs (Toggle)",
                            description: "Toggle the visibility of order drop-off location pins for easier route planning and clustering.",
                            shortcut: null,
                        },
                        {
                            icon: <ThemeShiftIcon />,
                            title: "Theme",
                            description: "Switch between available UI themes for the map to match user preferences or environmental conditions (e.g., dark mode).",
                            shortcut: null,
                        },
                        {
                            icon: <LocationSearch />,
                            title: "City Search",
                            description: "Quickly locate a city or region by typing its name. The map will auto-navigate to the specified location.",
                            shortcut: null,
                        },
                        {
                            icon: <ResetViewportDuotoneIcon />,
                            title: "Reset Viewport",
                            description: "Restores the map view to the default geographic center (Riyadh).",
                            shortcut: null,
                        },
                        {
                            icon: <FocusMapDuotoneIcon />,
                            title: "Area Focus",
                            description: "Zooms in on the selected drop-offs area for detailed inspection and task assignment.",
                            shortcut: "ALT + F",
                        },
                        {
                            icon: <ShowRouteDuotoneIcon />,
                            title: "Route",
                            description: "Draws an optimal route line between selected pickup and drop-off locations to assist in logistics planning.",
                            shortcut: "ALT + R",
                        },
                        {
                            icon: <AreaCoverageDuotoneIcon />,
                            title: "Area Coverage",
                            description: "Generates a polygonal overlay that highlights the geographic region covered by the selected drop-offs.",
                            shortcut: "ALT + G",
                        },
                        {
                            icon: <AssignOrdersDuotoneIcon />,
                            title: "Assign Orders",
                            description: "Assigns one or more selected orders to a chosen driver for fulfillment.",
                            shortcut: "ALT + A",
                        },
                        {
                            icon: <UnassignOrderDuotoneIcon />,
                            title: "Unassign Orders",
                            description: "Removes previously assigned orders from a driver and returns them to the unassigned pool.",
                            shortcut: "ALT + U",
                        },
                        {
                            icon: <MergeOrderDuotoneIcon />,
                            title: "Merge Orders",
                            description: "Merges multiple individual orders into a single shipment for streamlined handling and improved efficiency.",
                            shortcut: "ALT + M",
                        },
                        {
                            icon: <MergePublishOrderDuotoneIcon />,
                            title: "Merge Orders & Publish",
                            description: "Merges multiple orders into one shipment and immediately publishes it for dispatch, enabling faster processing and optimized resource utilization.",
                            shortcut: "ALT + P",
                        },
                    ],
                },
            ],
        }, ar: {
            dir: "rtl",
            title: "توثيق BigBrother",
            buttonText: "English",
            tableHeaders: {
                icon: "أيقونة",
                action: "الإجراء",
                description: "الوصف",
                shortcut: "الاختصار"
            },
            sections: [
                {
                    title: "نظرة عامة",
                    content:
                        "خريطة BigBrother (BB Map) هي أداة مركزية قوية ضمن بوابة برق الإدارية، مصممة للمراقبة والإدارة في الوقت الفعلي للعمليات اللوجستية. تم تصميم خريطة BB لتحقيق الكفاءة، حيث توفر نظرة عامة مرئية وواضحة لسلسلة التوريد، وتعرض الطلبات، والسائقين (يُطلق عليهم الفرسان)، ومواقعهم - كل ذلك في لوحة تحكم تفاعلية واحدة. تنقسم الواجهة إلى ثلاثة أقسام رئيسية: قسم الطلبات، وقسم الفرسان، والخريطة المركزية، حيث يقدم كل قسم أدوات محددة للمساعدة في إدارة الخدمات اللوجستية بسلاسة أكبر.",
                },
                {
                    title: "1. قسم الطلبات",
                    content:
                        "هذا القسم مخصص لإدارة جميع الطلبات الواردة والمستمرة. يُمكّن المستخدمين من استخدام أدوات للبحث عن الطلبات وتصفيتها وتحديدها وتتبعها بسهولة. التصميم بديهي، وبطاقات الطلبات مصممة لعرض تفاصيل الطلب الهامة بلمحة سريعة. يتضمن هيكل بطاقات الطلبات العناصر التالية:",
                    tags: [
                        {
                            classes: "w-fit text-[#3c4b64] text-[15px] px-1.5 py-0.5 rounded-md mt-2 font-semibold",
                            tag: "الشريط العلوي لكل بطاقة طلب يعرض معلومات أساسية تشمل رقم التتبع، ونوع الطلب، وحالة الدمج (ممثلة برمز 'M')، ونقطة ملونة تشير إلى مجموعة الشحنة."
                        },
                        {
                            classes: "w-fit text-[#3c4b64] text-[15px] px-1.5 py-0.5 rounded-md mt-2 font-semibold",
                            tag: "يتوفر زر أيقونة العين على كل بطاقة، مما يتيح للمستخدمين التبديل السريع بين العرض التفصيلي للطلب."
                        },
                        {
                            classes: "w-fit text-[#3c4b64] text-[15px] px-1.5 py-0.5 rounded-md mt-2 font-semibold",
                            tag: "يتم عرض حالة اتفاقية مستوى الخدمة (SLA) بوضوح في الزاوية اليمنى السفلية من البطاقة، مما يساعد على ضمان التسليم في الوقت المحدد."
                        },
                        {
                            classes: "w-fit text-[#3c4b64] text-[15px] px-1.5 py-0.5 rounded-md mt-2 font-semibold",
                            tag: "تظهر الطلبات المستلمة حديثًا مع رسوم متحركة (Ping) لجذب الانتباه بصريًا للمراجعة الفورية."
                        },
                        {
                            classes: "w-fit text-[#3c4b64] text-[15px] px-1.5 py-0.5 rounded-md mt-2 font-semibold",
                            tag: "لإدارة طلب، ما عليك سوى النقر على بطاقة الطلب المعنية. تتم إضافة الطلبات المحددة إلى قائمة 'الطلبات المحددة'، مما يتيح سهولة تنفيذ الإجراءات الجماعية والتتبع."
                        }
                    ],
                    subsections: [
                        { title: "الشريط العلوي", description: "يحتوي على شريط بحث عام وأزرار تنقل سريعة مثل 'الانتقال إلى الأعلى' و 'الانتقال إلى الأسفل'." },
                        { title: "الفلاتر", description: "تصفية قائمة الطلبات ديناميكيًا حسب معايير مثل حالة الشحنة، والتاجر، والمركز (Hub)، والنوع." },
                        { title: "الطلبات المحددة", description: "مساحة عمل مؤقتة تظهر فيها الطلبات التي تم النقر عليها، مما يتيح تجميعها لاتخاذ إجراءات جماعية." },
                        { title: "قائمة الطلبات", description: "القائمة الرئيسية لبطاقات الطلبات. يؤدي النقر على بطاقة إلى إضافتها إلى قائمة 'الطلبات المحددة'." },
                    ],
                },
                {
                    title: "2. قسم الفرسان (السائقين)",
                    content:
                        "يعكس هذا القسم وظائف قسم الطلبات ولكنه يركز بالكامل على إدارة موظفي الخدمات اللوجستية - الذين يشار إليهم بالفرسان. يسمح بالتتبع في الوقت الفعلي والإدارة الذكية لحالات السائقين ومهامهم.",
                    subsections: [
                        { title: "الشريط العلوي", description: "يحتوي على شريط بحث للعثور على سائقين محددين وأزرار تنقل." },
                        { title: "الفلاتر", description: "تصفية قائمة السائقين بناءً على سمات مثل المدينة، وحالة الجهاز، وحالة التشغيل (متاح، في مهمة، إلخ)." },
                        { title: "السائقون المختارون", description: "قائمة لحفظ السائقين المختارين، عادةً لتعيين مهام لهم." },
                        { title: "قائمة السائقين", description: "القائمة الرئيسية لبطاقات السائقين، والتي توضح حالتهم الحالية ومدى توفرهم." },
                    ],
                },
                {
                    title: "3. قسم الخريطة والإجراءات",
                    content:
                        "الخريطة المركزية هي القلب التشغيلي لواجهة خريطة BB. فهي توفر تمثيلاً مرئيًا مباشرًا للطلبات والسائقين والعمليات الجغرافية. بفضل عناصر التحكم والأدوات المدمجة، تتيح للمستخدمين إدارة الخدمات اللوجستية الميدانية من خلال التفاعل المباشر مع الخريطة. فيما يلي الوظائف المتاحة، مصنفة تحت علامات الخريطة وإجراءات شريط الأدوات.",
                    tags: [
                        {
                            classes: "w-fit text-[#3c4b64] text-[15px] px-1.5 py-0.5 rounded-md mt-2 font-semibold",
                            tag: "استخدم ميزة 'تحديد منطقة' عن طريق الضغط باستمرار على مفتاح Ctrl والنقر والسحب بالماوس لرسم منطقة تحديد على الخريطة. سيتم تحديد جميع الطلبات داخل المنطقة المحددة تلقائيًا."
                        },
                        {
                            classes: "w-fit text-[#3c4b64] text-[15px] px-1.5 py-0.5 rounded-md mt-2 font-semibold",
                            tag: "النقر على أي أيقونة (مثل سائق أو دبوس نقطة تسليم) على الخريطة سيضيفها تلقائيًا إلى قائمة التحديد الخاصة بك لاتخاذ إجراءات إضافية."
                        },
                        {
                            classes: "w-fit text-[#3c4b64] text-[15px] px-1.5 py-0.5 rounded-md mt-2 font-semibold",
                            tag: "تتحرك نقاط التسليم المحددة على الخريطة بتأثير ارتدادي (bounce) لتقديم مؤشر مرئي لحالتها."
                        }
                    ],
                    actions: [
                        {
                            icon: <DropoffDuotoneIcon />,
                            title: "الشحنات (تبديل)",
                            description: "تبديل رؤية أيقونات مجموعات الشحنات على الخريطة. مفيد لتقليل الفوضى البصرية أثناء التحليل.",
                            shortcut: null,
                        },
                        {
                            icon: <DriverDuotoneIcon />,
                            title: "السائقون (تبديل)",
                            description: "تمكين أو تعطيل رؤية جميع أيقونات السائقين على الخريطة للوعي التشغيلي في الوقت الفعلي.",
                            shortcut: null,
                        },
                        {
                            icon: <GroupDropoffDuotoneIcon />,
                            title: "نقاط التسليم (تبديل)",
                            description: "تبديل رؤية دبابيس مواقع تسليم الطلبات لتسهيل تخطيط المسار والتجميع.",
                            shortcut: null,
                        },
                        {
                            icon: <ThemeShiftIcon />,
                            title: "السمة (Theme)",
                            description: "التبديل بين سمات واجهة المستخدم المتاحة للخريطة لتتناسب مع تفضيلات المستخدم أو الظروف البيئية (مثل الوضع المظلم).",
                            shortcut: null,
                        },
                        {
                            icon: <LocationSearch />,
                            title: "البحث عن مدينة",
                            description: "تحديد موقع مدينة أو منطقة بسرعة عن طريق كتابة اسمها. ستنتقل الخريطة تلقائيًا إلى الموقع المحدد.",
                            shortcut: null,
                        },
                        {
                            icon: <ResetViewportDuotoneIcon />,
                            title: "إعادة تعيين العرض",
                            description: "يعيد عرض الخريطة إلى المركز الجغرافي الافتراضي - والذي يتم تعيينه عادةً إلى الرياض.",
                            shortcut: null,
                        },
                        {
                            icon: <FocusMapDuotoneIcon />,
                            title: "تركيز على منطقة",
                            description: "يقوم بالتكبير على منطقة نقاط التسليم المحددة للفحص التفصيلي وتعيين المهام.",
                            shortcut: "ALT + F",
                        },
                        {
                            icon: <ShowRouteDuotoneIcon />,
                            title: "المسار",
                            description: "يرسم خط مسار مثالي بين مواقع الاستلام والتسليم المحددة للمساعدة في التخطيط اللوجستي.",
                            shortcut: "ALT + R",
                        },
                        {
                            icon: <AreaCoverageDuotoneIcon />,
                            title: "تغطية المنطقة",
                            description: "ينشئ تراكبًا مضلعًا يبرز المنطقة الجغرافية التي تغطيها نقاط التسليم المحددة.",
                            shortcut: "ALT + G",
                        },
                        {
                            icon: <AssignOrdersDuotoneIcon />,
                            title: "تعيين الطلبات",
                            description: "يعيّن طلبًا واحدًا أو أكثر من الطلبات المحددة لسائق مختار لإنجازها.",
                            shortcut: "ALT + A",
                        },
                        {
                            icon: <UnassignOrderDuotoneIcon />,
                            title: "إلغاء تعيين الطلبات",
                            description: "يزيل الطلبات المعينة مسبقًا من السائق ويعيدها إلى مجموعة الطلبات غير المعينة.",
                            shortcut: "ALT + U",
                        },
                        {
                            icon: <MergeOrderDuotoneIcon />,
                            title: "دمج الطلبات",
                            description: "دمج العديد من الطلبات الفردية في شحنة واحدة لتسهيل التعامل وتحسين الكفاءة.",
                            shortcut: "ALT + M",
                        },
                        {
                            icon: <MergePublishOrderDuotoneIcon />,
                            title: "دمج الطلبات والنشر",
                            description: "دمج الطلبات المتعددة في شحنة واحدة ونشرها على الفور للإرسال، مما يتيح معالجة أسرع واستخدامًا مثاليًا للموارد.",
                            shortcut: "ALT + P",
                        },
                    ],
                },
            ],
        }
    };
    const currentDoc = docContent[lang];

    return (
        <>
            <button
                onClick={() => setOpenD(true)}
                className="bg-white p-1 border-2 border-t-0 border-[#c2beb3] rounded-b-md shadow-md hover:bg-slate-100 transition-colors cursor-pointer"
                title="Open Documentation"
                aria-label="Open Documentation"
            >
                <DocsIcon />
            </button>

            <Dialog fullScreen open={openD} onClose={() => setOpenD(false)} PaperProps={{ className: "bg-slate-50" }}>
                <div className="flex flex-col h-screen" dir={currentDoc.dir}>
                    {/* Main Content: Centered on large screens for readability. */}
                    <div className=" mx-[5vw] my-[5vh]">
                        <header className={`sticky top-0 bg-orange-100 rounded-t-lg backdrop-blur-lg border-b border-slate-200 z-10 shadow-sm z-10 px-4  py-3 flex items-center justify-between ${currentDoc.dir === "ar" ? "flex-row-reverse" : ""} `}>
                            <div className="flex items-center gap-4">
                                <button onClick={() => setOpenD(false)} aria-label="Go back" className="p-2 rounded-full bg-orange-200 text-orange-500 hover:bg-orange-300 hover:text-slate-900 transition-colors">
                                    {currentDoc.dir === "ltr" ? <ArrowBackIcon /> : <ArrowForwardIcon />}
                                </button>
                                <h1 className="text-xl sm:text-2xl font-bold text-[#3c4b64]">{currentDoc.title}</h1>
                            </div>
                            <button onClick={toggleLang} className="bg-orange-300 shadow-lg hover:bg-orange-400 text-slate-700 text-sm font-semibold px-4 py-2 rounded-lg transition-all duration-200 shadow-sm">
                                {currentDoc.buttonText}
                            </button>
                        </header>
                    </div>
                    <main className={`flex-grow overflow-y-auto scrollbar-custom-widget mb-[10vh] rounded-lg ${currentDoc.dir === "ltr" ? "ml-[6vw] mr-[7vw]" : "ml-[7vw] mr-[6vw]"}`}>
                        <div className=" mx-auto space-y-12">
                            {currentDoc.sections.map((section, index) => (
                                <section key={index} className="bg-white p-3  rounded-xl ">
                                    <h2 className="text-[21px] font-bold text-[#3c4b64] border-b border-slate-200 pb-4 mb-6">{section.title}</h2>
                                    <p className="text-slate-600 leading-loose text-base whitespace-pre-line">{section.content}</p>
                                    {section.tags && (
                                        <div className="">
                                            {section.tags.map((sub, subIndex) => (
                                                <li key={subIndex} className={`${sub.classes}`}>{sub.tag}</li>
                                            ))}
                                        </div>
                                    )}
                                    {section.subsections && (
                                        <div className="mt-6 grid grid-cols-1 md:grid-cols-4 gap-5 ">
                                            {section.subsections.map((sub, subIndex) => (
                                                // Subsection Card: Uses a more subtle one-sided border for hierarchy.
                                                <div key={subIndex} className={`bg-orange-50/80 p-5 rounded-md ${currentDoc.dir === 'ltr' ? 'border-l-2 border-orange-300' : 'border-r-2 border-orange-300'}`}>
                                                    <h3 className="font-semibold text-lg text-slate-800">{sub.title}</h3>
                                                    <p className="text-slate-600 text-sm mt-2 leading-relaxed">{sub.description}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                    {/* Table: Wrapped for better styling (rounded corners, shadow) and responsiveness. */}
                                    {section.actions && (
                                        <div className="mt-8 overflow-x-auto">
                                            {/* Table: No shadow, just a clean border for encapsulation. */}
                                            <div className="inline-block min-w-full align-middle">
                                                <div className="overflow-hidden  rounded-lg">
                                                    <table className={`min-w-full divide-y divide-slate-200 ${lang === "ar" ? "text-right" : "text-left"}`}>
                                                        <thead className="bg-slate-100">
                                                            <tr>
                                                                <th scope="col" className="py-3.5 px-4 text-sm font-semibold text-slate-700 w-16">{currentDoc.tableHeaders.icon}</th>
                                                                <th scope="col" className="py-3.5 px-3 text-sm font-semibold text-slate-700">{currentDoc.tableHeaders.action}</th>
                                                                <th scope="col" className="py-3.5 px-3 text-sm font-semibold text-slate-700">{currentDoc.tableHeaders.description}</th>
                                                                <th scope="col" className="py-3.5 px-4 text-sm font-semibold text-slate-700">{currentDoc.tableHeaders.shortcut}</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody className="divide-y divide-slate-200 bg-white">
                                                            {section.actions.map((action, actionIndex) => (
                                                                <tr key={actionIndex} className="hover:bg-slate-50 transition-colors">
                                                                    <td className="whitespace-nowrap py-4 px-4 text-center text-slate-500">{action.icon}</td>
                                                                    <td className="whitespace-nowrap py-4 px-3 text-sm font-medium text-slate-900">{action.title}</td>
                                                                    <td className="py-4 px-3 text-sm text-slate-600 leading-relaxed">{action.description}</td>
                                                                    <td className="whitespace-nowrap py-4 px-4 text-sm">
                                                                        {action.shortcut ? <Shortcut>{action.shortcut}</Shortcut> : <span className="text-slate-400">—</span>}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                </section>
                            ))}
                        </div>
                    </main>
                </div>
            </Dialog>
        </>
    );
}











